'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { useAI } from '@/context/AIContext'

interface Capability {
  icon: React.ElementType
  label: string
  query: string
}

// Default capabilities for general pages
const DEFAULT_CAPABILITIES: Capability[] = [
  { icon: Icons.helpCircle, label: 'What can you help me with?', query: 'What can you help me with?' },
  { icon: Icons.activity, label: 'Show recent activity', query: 'Show me recent activity' },
  { icon: Icons.fileText, label: 'Generate a report', query: 'Generate a report' },
  { icon: Icons.brain, label: 'Create a policy', query: 'Help me create a new policy' },
  { icon: Icons.lightbulb, label: 'Show insights', query: 'Show me AI insights' },
  { icon: Icons.info, label: 'Explain this page', query: 'Explain this page to me' },
]

// Call analysis specific capabilities
const CALL_ANALYSIS_CAPABILITIES: Capability[] = [
  { icon: Icons.barChart, label: "What's the overall score?", query: "What's the overall score for this call and what does it mean?" },
  { icon: Icons.sparkles, label: 'Summarize this call', query: 'Give me a summary of this call including key outcomes and customer sentiment.' },
  { icon: Icons.alertTriangle, label: 'What went wrong?', query: 'What were the main issues or missed opportunities in this call?' },
  { icon: Icons.clock, label: 'Show key moments', query: 'What were the key moments in this call? Show me important timestamps.' },
  { icon: Icons.checkCircle, label: 'What follow-ups are needed?', query: 'What follow-up tasks should be done after this call?' },
  { icon: Icons.brain, label: 'How can the rep improve?', query: 'What coaching recommendations do you have for the sales rep based on this call?' },
  { icon: Icons.messageSquare, label: 'Find in transcript', query: 'Help me find specific parts of the conversation. What topics were discussed?' },
  { icon: Icons.users, label: 'Customer sentiment', query: 'What was the customer sentiment during this call? Were there any concerns?' },
]

interface CapabilityBubblesProps {
  onSelect: (query: string) => void
}

export function CapabilityBubbles({ onSelect }: CapabilityBubblesProps) {
  const { callAnalysisData, currentPageContext } = useAI()
  
  // Use call analysis capabilities when on call analysis page with data
  const isCallAnalysisPage = currentPageContext.startsWith('/analyze/')
  const capabilities = isCallAnalysisPage && callAnalysisData 
    ? CALL_ANALYSIS_CAPABILITIES 
    : DEFAULT_CAPABILITIES
  
  return (
    <div className="flex flex-wrap justify-center gap-2 p-2">
      {/* Show context label for call analysis */}
      {isCallAnalysisPage && callAnalysisData && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full text-center mb-2"
        >
          <span className="text-xs text-muted-foreground">
            Ask me about this call recording:
          </span>
        </motion.div>
      )}
      
      {capabilities.map((cap, i) => {
        const Icon = cap.icon
        return (
          <motion.button
            key={`${isCallAnalysisPage ? 'call' : 'default'}-${i}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.25, ease: 'easeOut' }}
            onClick={() => onSelect(cap.query)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-full',
              'bg-white border',
              isCallAnalysisPage && callAnalysisData
                ? 'border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300'
                : 'border-brand-cornflower/20 hover:bg-brand-cornflower/10 hover:border-brand-cornflower/40',
              'text-sm text-brand-navy',
              'shadow-sm',
              'transition-all duration-200',
              'hover:shadow-md',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50'
            )}
          >
            <Icon 
              className={cn(
                "h-4 w-4",
                isCallAnalysisPage && callAnalysisData ? "text-emerald-600" : "text-brand-cornflower"
              )} 
              strokeWidth={1.5} 
            />
            <span className="text-xs sm:text-sm font-medium">{cap.label}</span>
          </motion.button>
        )
      })}
    </div>
  )
}

